import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, FileCheck2, FolderPlus, Link2, MailPlus, Reply, Zap } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { COMM_TYPE, fmtDate, fmtDateTime } from "../lib/labels";
import { EmptyState, PageHeader, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const SIM_KINDS = [
  { id: "factura", label: "Recepción de factura" },
  { id: "vencimiento", label: "Aviso de vencimiento" },
  { id: "municipal", label: "Comunicación municipal" },
  { id: "info", label: "Solicitud de información" },
  { id: "firma", label: "Confirmación de firma" },
];

export default function Comunicaciones() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [direction, setDirection] = useState("");
  const [selected, setSelected] = useState(null);
  const [dialog, setDialog] = useState(null); // relacionar | crear_expediente | crear_tarea | evidencia | responder
  const [form, setForm] = useState({});
  const [confirmSend, setConfirmSend] = useState(false);

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["communications", q, direction],
    queryFn: async () => (await api.get("/communications", { params: { q: q || undefined, direction: direction || undefined } })).data,
  });
  const { data: exps } = useQuery({ queryKey: ["expedientes-mini"], queryFn: async () => (await api.get("/expedientes")).data });
  const { data: reqs } = useQuery({
    queryKey: ["evreqs-for-comm", selected?.expediente_id],
    queryFn: async () => (await api.get("/evidence-requirements", { params: { expediente_id: selected.expediente_id } })).data,
    enabled: !!selected?.expediente_id,
  });

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["dashboard-badges"] }); };

  const simulate = async (kind) => {
    try {
      const r = await api.post("/communications/simulate", { kind });
      toast.success("Comunicación simulada recibida", { description: r.data.subject });
      refresh();
      setSelected(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doAction = async (action, payload = {}) => {
    try {
      const r = await api.post(`/communications/${selected.id}/action`, { action, ...payload });
      toast.success("Acción ejecutada");
      if (action === "crear_expediente" && r.data.expediente) navigate(`/expedientes/${r.data.expediente.id}`);
      setDialog(null); setForm({}); setConfirmSend(false);
      refresh();
      const updated = await api.get(`/communications/${selected.id}`);
      setSelected(updated.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="comunicaciones-page">
      <PageHeader
        title="Comunicaciones"
        subtitle="Bandeja demo. Las recepciones pueden disparar tareas, alertas y expedientes. Los envíos externos requieren confirmación humana."
      />

      <div className="ts-card mb-4 p-3" data-testid="simulator-panel">
        <p className="ts-caption mb-2 flex items-center gap-1"><Zap className="h-3 w-3" /> Simulador de recepción (demo)</p>
        <div className="flex flex-wrap gap-1.5">
          {SIM_KINDS.map((k) => (
            <Button key={k.id} size="sm" variant="outline" onClick={() => simulate(k.id)} data-testid={`simulate-${k.id}`}>
              <MailPlus className="mr-1 h-3.5 w-3.5" /> {k.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por asunto, contenido o remitente…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-72" data-testid="comm-search-input" />
        <Select value={direction} onValueChange={(v) => setDirection(v === "todas" ? "" : v)}>
          <SelectTrigger className="h-8 w-40" data-testid="comm-direction-filter"><SelectValue placeholder="Dirección" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            <SelectItem value="recibida">Recibidas</SelectItem>
            <SelectItem value="enviada">Enviadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2">
          {isLoading ? (
            <p className="py-10 text-center text-sm text-slate-500">Cargando bandeja…</p>
          ) : !items?.length ? (
            <EmptyState title="Bandeja vacía" description="Usa el simulador para recibir comunicaciones de prueba." testid="comms-empty" />
          ) : (
            <div className="ts-card max-h-[70vh] divide-y divide-slate-100 overflow-y-auto">
              {items.map((c) => (
                <button key={c.id} onClick={() => setSelected(c)} data-testid={`comm-item-${c.id}`}
                  className={`block w-full p-3 text-left transition-colors hover:bg-slate-50 ${selected?.id === c.id ? "bg-blue-50/60" : ""}`}>
                  <div className="flex items-center gap-2">
                    {c.action_required && c.direction === "recibida" && c.status === "recibida" && <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" title="Acción requerida" />}
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{c.subject}</p>
                    <span className="shrink-0 text-[10px] text-slate-400">{fmtDate(c.date)}</span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{c.sender_name} · {COMM_TYPE[c.type] || c.type}{c.expediente ? ` · ${c.expediente.code}` : " · sin clasificar"}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {!selected ? (
            <EmptyState title="Selecciona una comunicación" description="El detalle y las acciones disponibles aparecerán aquí." testid="comm-detail-empty" />
          ) : (
            <div className="ts-card p-5" data-testid="comm-detail">
              <div className="flex flex-wrap items-center gap-2">
                <ToneChip label={COMM_TYPE[selected.type] || selected.type} tone={selected.direction === "recibida" ? "info" : "muted"} />
                <ToneChip label={selected.status} tone={selected.status === "recibida" ? "warning" : "muted"} />
                {selected.action_required && <ToneChip label="Acción requerida" tone="danger" />}
                {selected.detected_due_date && <ToneChip label={`Fecha detectada: ${fmtDate(selected.detected_due_date)}`} tone="warning" />}
              </div>
              <h2 className="mt-3 font-display text-lg font-semibold text-slate-900" data-testid="comm-subject">{selected.subject}</h2>
              <p className="mt-1 text-xs text-slate-500">De: {selected.sender_name} &lt;{selected.sender_email}&gt; · Para: {(selected.recipients || []).join(", ")} · {fmtDateTime(selected.date)}</p>
              {selected.attachments?.length > 0 && <p className="mt-1 text-xs text-slate-500">Adjuntos: {selected.attachments.join(", ")}</p>}
              <div className="mt-3 rounded-sm border border-slate-100 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700" data-testid="comm-content">{selected.content}</div>
              {selected.expediente && (
                <p className="mt-2 text-xs">Relacionada con: <button className="font-medium text-blue-700 hover:underline" onClick={() => navigate(`/expedientes/${selected.expediente_id}`)} data-testid="comm-exp-link">{selected.expediente.code} — {selected.expediente.title}</button></p>
              )}

              <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-4">
                <Button size="sm" variant="outline" onClick={() => { setForm({}); setDialog("relacionar"); }} data-testid="comm-action-relacionar"><Link2 className="mr-1 h-3.5 w-3.5" /> Relacionar con expediente</Button>
                <Button size="sm" variant="outline" onClick={() => { setForm({ title: selected.subject }); setDialog("crear_expediente"); }} data-testid="comm-action-crear-expediente"><FolderPlus className="mr-1 h-3.5 w-3.5" /> Crear expediente</Button>
                <Button size="sm" variant="outline" onClick={() => { setForm({ title: `Gestionar: ${selected.subject}` }); setDialog("crear_tarea"); }} data-testid="comm-action-crear-tarea"><FileCheck2 className="mr-1 h-3.5 w-3.5" /> Crear tarea</Button>
                {selected.expediente_id && (
                  <Button size="sm" variant="outline" onClick={() => { setForm({}); setDialog("evidencia"); }} data-testid="comm-action-evidencia"><FileCheck2 className="mr-1 h-3.5 w-3.5" /> Usar como evidencia</Button>
                )}
                {selected.direction === "recibida" && (
                  <Button size="sm" onClick={() => { setForm({ content: "Recibido. Lo gestionamos y te damos respuesta en el plazo indicado." }); setDialog("responder"); }} data-testid="comm-action-responder"><Reply className="mr-1 h-3.5 w-3.5" /> Responder</Button>
                )}
                {selected.status !== "archivada" && (
                  <Button size="sm" variant="ghost" onClick={() => doAction("archivar")} data-testid="comm-action-archivar"><Archive className="mr-1 h-3.5 w-3.5" /> Archivar</Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="comm-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog === "relacionar" && "Relacionar con expediente"}
              {dialog === "crear_expediente" && "Crear expediente desde comunicación"}
              {dialog === "crear_tarea" && "Crear tarea desde comunicación"}
              {dialog === "evidencia" && "Usar como evidencia"}
              {dialog === "responder" && "Confirmar envío de respuesta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {(dialog === "relacionar" || dialog === "crear_tarea") && (
              <div>
                <Label>Expediente</Label>
                <Select value={form.expediente_id || selected?.expediente_id || ""} onValueChange={(v) => setForm({ ...form, expediente_id: v })}>
                  <SelectTrigger data-testid="comm-exp-select"><SelectValue placeholder="Seleccionar expediente…" /></SelectTrigger>
                  <SelectContent>{(exps || []).slice(0, 60).map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.title.slice(0, 45)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {(dialog === "crear_expediente" || dialog === "crear_tarea") && (
              <div><Label>Título</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="comm-title-input" /></div>
            )}
            {dialog === "evidencia" && (
              <div>
                <Label>Requisito de evidencia del expediente</Label>
                <Select value={form.requirement_id || ""} onValueChange={(v) => setForm({ ...form, requirement_id: v })}>
                  <SelectTrigger data-testid="comm-req-select"><SelectValue placeholder="Seleccionar requisito…" /></SelectTrigger>
                  <SelectContent>{(reqs || []).filter((r) => r.status !== "aceptada").map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.status})</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-500">La comunicación quedará vinculada como evidencia de tipo email y pasará a revisión.</p>
              </div>
            )}
            {dialog === "responder" && (
              <>
                <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="send-preview">
                  <p><strong>Para:</strong> {selected?.sender_email}</p>
                  <p><strong>Asunto:</strong> RE: {selected?.subject}</p>
                  <p><strong>Adjuntos:</strong> ninguno</p>
                </div>
                <div><Label>Contenido</Label><Textarea value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="reply-content-input" /></div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={confirmSend} onCheckedChange={setConfirmSend} data-testid="send-confirm-checkbox" />
                  Confirmo el envío de esta comunicación externa (requiere confirmación humana)
                </label>
              </>
            )}
            <Button
              className="w-full"
              data-testid="comm-action-confirm"
              disabled={
                (dialog === "relacionar" && !(form.expediente_id || selected?.expediente_id)) ||
                (dialog === "crear_tarea" && !form.title?.trim()) ||
                (dialog === "crear_expediente" && !form.title?.trim()) ||
                (dialog === "evidencia" && !form.requirement_id) ||
                (dialog === "responder" && !confirmSend)
              }
              onClick={() => {
                if (dialog === "relacionar") doAction("relacionar", { expediente_id: form.expediente_id || selected.expediente_id });
                if (dialog === "crear_expediente") doAction("crear_expediente", { title: form.title });
                if (dialog === "crear_tarea") doAction("crear_tarea", { title: form.title, expediente_id: form.expediente_id });
                if (dialog === "evidencia") doAction("usar_como_evidencia", { requirement_id: form.requirement_id });
                if (dialog === "responder") doAction("responder", { content: form.content, confirm: true });
              }}
            >
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
